import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PatientRow from "./PatientRow";
import { fetchPatients } from "../../store/patients-slice";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import { trashPatients } from "../../util/patientsApi";
import { notificationActions } from "../../store/notification-slice";
import ConfirmModal from "../modals/ConfirmModal";

function PatientsList(props) {
  const [query, setQuery] = useState("");
  const [sortingQuery, setSortingQuery] = useState("patient_name");
  const [sortingAscending, setSortingAscending] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedPatientIds, setSelectedPatientIds] = useState(new Set());
  const [trashConfirmOpen, setTrashConfirmOpen] = useState(false);
  const [isTrashing, setIsTrashing] = useState(false);
  const search_keys = [
    "patient_name",
    "patient_email",
    "patient_phone",
    "patient_condition",
  ];
  const patientsList = useSelector((state) => state.patients.patientsList);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchPatients());
  }, []);
  const search = (data) => {
    const filtered_patients = data
      .filter((patient) =>
        search_keys.some((key) =>
          patient[key]?.toLowerCase().includes(query.toLowerCase())
        )
      )
      .sort((a, b) =>
        sortingAscending
          ? a[sortingQuery].localeCompare(b[sortingQuery])
          : b[sortingQuery].localeCompare(a[sortingQuery])
      );
    return filtered_patients;
  };

  const filteredPatients = patientsList ? search(patientsList) : [];
  const totalRows = filteredPatients.length;
  const paginatedPatients = filteredPatients.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  useEffect(() => {
    setPage(0);
  }, [query, rowsPerPage]);

  const togglePatientSelection = (id) => {
    setSelectedPatientIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAllOnPage = () => {
    const pageIds = paginatedPatients.map((p) => p.patient_id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedPatientIds.has(id));
    if (allSelected) {
      setSelectedPatientIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedPatientIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };
  const selectAllChecked = paginatedPatients.length > 0 && paginatedPatients.every((p) => selectedPatientIds.has(p.patient_id));
  const selectAllIndeterminate = paginatedPatients.some((p) => selectedPatientIds.has(p.patient_id)) && !selectAllChecked;

  const handleTrashConfirm = () => {
    const ids = Array.from(selectedPatientIds);
    if (ids.length === 0) return;
    setIsTrashing(true);
    trashPatients(ids)
      .then(() => {
        setSelectedPatientIds(new Set());
        setTrashConfirmOpen(false);
        dispatch(fetchPatients());
        dispatch(notificationActions.setNotification({ show: true, status: "success", message: "Moved to trash." }));
      })
      .catch((err) => {
        dispatch(
          notificationActions.setNotification({
            show: true,
            status: "error",
            message: err?.response?.data?.reason || err?.message || "Failed to move to trash.",
          })
        );
      })
      .finally(() => setIsTrashing(false));
  };

  return (
    <section className="patient-table container">
      <div className="selection-toolbar-slot">
        {selectedPatientIds.size > 0 && (
          <div className="selection-toolbar">
            <span className="selection-toolbar__count">{selectedPatientIds.size} patient(s) selected</span>
            <button type="button" className="svg-btn selection-toolbar__btn" onClick={() => setTrashConfirmOpen(true)}>
              Move to trash
            </button>
            <button type="button" className="svg-btn selection-toolbar__btn selection-toolbar__btn--secondary" onClick={() => setSelectedPatientIds(new Set())}>
              Clear selection
            </button>
          </div>
        )}
      </div>
      <table>
        <tbody>
          <tr>
            <td colSpan="5" style={{ height: "fit-content" }}>
              <input
                id="search-bar"
                placeholder="Search..."
                onChange={(e) => setQuery(e.target.value)}
              ></input>
            </td>
          </tr>

          <tr>
            <th className="table-header-checkbox">
              <input
                type="checkbox"
                checked={selectAllChecked}
                ref={(el) => el && (el.indeterminate = selectAllIndeterminate)}
                onChange={selectAllOnPage}
                aria-label="Select all on this page"
              />
            </th>
            <th>
              Name
              {sortingQuery == "patient_name" ? (
                sortingAscending ? (
                  <Icon
                    onClick={() => setSortingAscending(false)}
                    icon="fluent:arrow-sort-down-24-filled"
                  />
                ) : (
                  <Icon
                    onClick={() => setSortingAscending(true)}
                    icon="fluent:arrow-sort-down-24-filled"
                    vFlip={true}
                  />
                )
              ) : (
                <Icon
                  onClick={() => {
                    setSortingQuery("patient_name");
                    setSortingAscending(true);
                  }}
                  icon="basil:sort-solid"
                />
              )}
            </th>
            <th>
              Email
              {sortingQuery == "patient_email" ? (
                sortingAscending ? (
                  <Icon
                    onClick={() => setSortingAscending(false)}
                    icon="fluent:arrow-sort-down-24-filled"
                  />
                ) : (
                  <Icon
                    onClick={() => setSortingAscending(true)}
                    icon="fluent:arrow-sort-down-24-filled"
                    vFlip={true}
                  />
                )
              ) : (
                <Icon
                  onClick={() => {
                    setSortingQuery("patient_email");
                    setSortingAscending(true);
                  }}
                  icon="basil:sort-solid"
                />
              )}
            </th>
            <th>Condition</th>
            <th>Notes</th>
          </tr>
          {paginatedPatients.map((patient) => (
            <PatientRow
              key={patient.patient_id}
              patient={patient}
              selected={selectedPatientIds.has(patient.patient_id)}
              onToggle={togglePatientSelection}
            />
          ))}
        </tbody>
      </table>
      <div className="table-pagination">
        <span>Rows per page:</span>
        <select
          value={rowsPerPage}
          onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
        <span className="pagination-range">
          {totalRows === 0 ? '0-0 of 0' : `${page * rowsPerPage + 1}-${Math.min(page * rowsPerPage + rowsPerPage, totalRows)} of ${totalRows}`}
        </span>
        <div className="pagination-actions">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            aria-label="Previous page"
          >
            <Icon icon="mdi:chevron-left" />
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(Math.ceil(totalRows / rowsPerPage) - 1, p + 1))}
            disabled={page >= Math.ceil(totalRows / rowsPerPage) - 1 || totalRows === 0}
            aria-label="Next page"
          >
            <Icon icon="mdi:chevron-right" />
          </button>
        </div>
      </div>
      {trashConfirmOpen && (
        <ConfirmModal
          message={`Move ${selectedPatientIds.size} patient(s) to trash? They will be permanently deleted after 30 days.`}
          acceptHandler={handleTrashConfirm}
          rejectHandler={() => !isTrashing && setTrashConfirmOpen(false)}
          isLoading={isTrashing}
          confirmLabel={isTrashing ? undefined : "Move to trash"}
        />
      )}
    </section>
  );
}

export default PatientsList;
